import { enUS } from "date-fns/locale";
import type { DayPickerLocale } from "react-day-picker";
import { StatusText, StatusColor, Status } from "../views/FieldOfService/types";
import { DateRange } from "../components/DatePicker/datePicker.type";
import { minDate } from "../components/DatePicker/helper";
import { maxDate } from "../components/DatePicker/helper";
import moment, { Moment } from "moment";
import { AppContextValue } from "./types";
import { ISLOADING } from "../hooks/constants";
import { logger } from "../utils/logger";
import Strings from "../values/strings";
import { InstructionLine } from "../components/CSVDropZone";
import { AxiosResponse } from "axios";
import Colors from "../values/colors";

export const STATUS = {
  TOOL: {
    DEACTIVE: -1,
    UNAVAILABLE: 0,
    ACTIVE: 1,
    CHECKEDOUT: 2,
    BOOKED: 5,
    STOLEN: 91,
    DAMAGED: 92,
    LOST: 93,
    DELETE: 99,
    OUTOFSERVICES: 94,
    CANCELLED: 97,
    DEFULT: 95,
    RETURN_REQUEST: 42,
    REQUESTDELETEPENDDING: 95,
    RETURN_PENDING: 19,
    TRANSFER_PENDING: 17,
    TRANSFERRED: 31,
    TRANSFER_REQUEST: 32,
    // API transfer sub-section marker: the receiver has checked out a transferred asset.
    // Carried per-serial in the order's transferredItems[].transferStatus (DV-1894). A serial
    // with this marker renders as "Transferred" even on the sender's checkout order. (DV-1961)
    TRANSFERRED_MARKER: 15,
  },
};

export type ToolStatusMeta = {
  text: string;
  color: string;
};

export const resolveBaseToolStatus = (
  status: number | undefined,
  actionPending: number | undefined,
): ToolStatusMeta => {
  if (
    actionPending === STATUS.TOOL.TRANSFER_PENDING &&
    status === STATUS.TOOL.CHECKEDOUT
  ) {
    return {
      text: Strings.assetStatus.transferInProgress,
      color: Colors.Status.TRANSFER_REQUEST,
    };
  }
  if (
    actionPending === STATUS.TOOL.RETURN_PENDING &&
    status === STATUS.TOOL.CHECKEDOUT
  ) {
    return {
      text: Strings.assetStatus.returnValidationPending,
      color: Colors.Status.RETURN_REQUEST,
    };
  }

  switch (status) {
    case STATUS.TOOL.ACTIVE:
      return {
        text: Strings.assetStatus.available,
        color: Colors.Status.AVAILABLE,
      };
    case STATUS.TOOL.CHECKEDOUT:
      return {
        text: Strings.assetStatus.checkedOut,
        color: Colors.Status.CHECKEDOUT,
      };
    case STATUS.TOOL.UNAVAILABLE:
      return {
        text: Strings.assetStatus.pending,
        color: Colors.Status.UNAVAILABLE,
      };
    case STATUS.TOOL.BOOKED:
      return {
        text: Strings.assetStatus.reserved,
        color: Colors.Status.RESERVED,
      };
    case STATUS.TOOL.STOLEN:
      return { text: Strings.assetStatus.stolen, color: Colors.Status.STOLEN };
    case STATUS.TOOL.DAMAGED:
      return {
        text: Strings.assetStatus.damaged,
        color: Colors.Status.DAMAGED,
      };
    case STATUS.TOOL.LOST:
      return { text: Strings.assetStatus.lost, color: Colors.Status.LOST };
    case STATUS.TOOL.DELETE:
      return {
        text: Strings.assetStatus.deleted,
        color: Colors.Status.DELETED,
      };
    case STATUS.TOOL.CANCELLED:
      return {
        text: Strings.assetStatus.cancelled,
        color: Colors.Status.CANCELLED,
      };
    case STATUS.TOOL.OUTOFSERVICES:
      return {
        text: Strings.assetStatus.outForService,
        color: Colors.Status.OUTOFSERVICES,
      };
    case STATUS.TOOL.RETURN_REQUEST:
    case STATUS.TOOL.RETURN_PENDING:
      return {
        text: Strings.assetStatus.returnValidationPending,
        color: Colors.Status.RETURN_REQUEST,
      };
    case STATUS.TOOL.TRANSFER_PENDING:
      return {
        text: Strings.assetStatus.transferInProgress,
        color: Colors.Status.TRANSFER_REQUEST,
      };
    case STATUS.TOOL.REQUESTDELETEPENDDING:
      return {
        text: Strings.assetStatus.deletionRequested,
        color: Colors.Status.OUTOFSERVICES,
      };
    case STATUS.TOOL.TRANSFERRED:
      return {
        text: Strings.assetStatus.transferred,
        color: Colors.Status.TRANSFERRED,
      };
    case STATUS.TOOL.TRANSFER_REQUEST:
      return {
        text: Strings.assetStatus.transferInProgress,
        color: Colors.Status.TRANSFER_REQUEST,
      };
    default:
      return { text: "", color: "" };
  }
};

// Type guard function: Properly narrows type from {} | AppContextValue to AppContextValue
export const isAppContextValue = (
  context: AppContextValue | Record<string, never>,
): context is AppContextValue => {
  return (
    context !== null &&
    typeof context === "object" &&
    "dispatch" in context &&
    typeof context.dispatch === "function" &&
    "state" in context
  );
};

// =====================
// Global Loading Utility
// =====================

/**
 * Creates a function to set global loading state via AppContext
 * @param context - AppContext value
 * @param hookName - Name of the calling hook for logging
 * @returns Function to set loading state
 */
export const createSetGlobalLoading = (
  context: AppContextValue | Record<string, never>,
  hookName: string,
): ((loading: boolean) => void) => {
  return (loading: boolean): void => {
    if (!isAppContextValue(context)) {
      logger.error(`[ERROR][${hookName}](setGlobalLoading)`, {
        error: "AppContext or dispatch is not available.",
      });
      return;
    }
    context.dispatch({
      type: ISLOADING,
      payload: loading,
    });
  };
};

// Status renderer component for service checklists
export const getStatusInfo = (status: number) => {
  switch (status) {
    case Status.PUBLISHED:
      return {
        text: StatusText.PUBLISHED,
        color: StatusColor.PUBLISHED,
      };
    case Status.DRAFT:
      return {
        text: StatusText.DRAFT,
        color: StatusColor.DRAFT,
      };
    default:
      return {
        text: "-",
        color: "grey",
      };
  }
};

// Calculate the height based on the provided value
export const calculateHeightinVH = (heightValue: string) => {
  if (heightValue.includes("100vh")) {
    // Extract the value to subtract (e.g., '7.625rem' from '100vh-7.625rem')
    const subtractValue = heightValue.replace("100vh-", "");
    // Ensure positive calculation: 100vh - 7.625rem
    return `calc(100vh - ${subtractValue})`;
  }
  return heightValue;
};

// Function to format date
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const ISO_TIMESTAMP_DETAIL_DISPLAY_FORMAT = "DD/MM/YYYY, h:mm a";
export const formatIsoTimestampForDetailDisplay = (
  isoTimestamp: string | null | undefined,
): string => {
  if (!isoTimestamp) {
    return "-";
  }
  const parsed: Moment = moment(isoTimestamp);
  if (!parsed.isValid()) {
    return "-";
  }
  return parsed.format(ISO_TIMESTAMP_DETAIL_DISPLAY_FORMAT);
};

export const customLocale = {
  ...enUS,
  localize: {
    ...enUS.localize,
    day: (weekNumber: 0 | 1 | 2 | 3 | 4 | 5 | 6): string =>
      ["S", "M", "T", "W", "T", "F", "S"][weekNumber],
    ordinalNumber:
      enUS.localize?.ordinalNumber ??
      ((ordinalNumber: number) => `${ordinalNumber}`),
    era: enUS.localize?.era ?? ((era: number) => `${era}`),
    quarter: enUS.localize?.quarter ?? ((quarter: number) => `${quarter}`),
    month: enUS.localize?.month ?? ((month: number) => `${month}`),
    dayPeriod: enUS.localize?.dayPeriod ?? ((month: number) => `${month}`),
  },
} as Partial<DayPickerLocale>;

export const isWholeMonth = (dateRange: DateRange): boolean => {
  const { from, to } = dateRange;

  // Return false if `from` is undefined
  if (!from) {
    return false;
  }

  // Get today's date
  const today: Date = new Date();

  // Create Date objects for the start and end dates (ignoring time)
  const startDate: Date = new Date(
    from.getFullYear(),
    from.getMonth(),
    from.getDate(),
  );
  const endDate: Date = to
    ? new Date(to.getFullYear(), to.getMonth(), to.getDate())
    : today;

  // Check if the start date is the first day of the month
  const isStartFirstDayOfMonth: boolean = startDate.getDate() === 1;

  // Get the last day of the month for the endDate
  const lastDayOfMonth: number = new Date(
    endDate.getFullYear(),
    endDate.getMonth() + 1,
    0,
  ).getDate();

  // Check if both dates are in the same month and year
  const isSameMonthAndYear: boolean =
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear();

  // Check if the end date is the last day of the month or today (if it's the current month)
  const isEndLastDayOrToday: boolean =
    endDate.getDate() === lastDayOfMonth ||
    (endDate.getDate() === today.getDate() &&
      endDate.getMonth() === today.getMonth() &&
      endDate.getFullYear() === today.getFullYear());

  // Return true if all conditions are met
  return isStartFirstDayOfMonth && isEndLastDayOrToday && isSameMonthAndYear;
};

export const getFirstAndLastDayOfMonth = (
  date: Date,
  absoluteRange?: boolean,
): DateRange => {
  // Returns the 1st and last day of the month extracted from the received date object
  const momentDate: Moment = moment(date);

  const firstDayOfMonth: Moment = momentDate.clone().startOf("month");
  const lastDayOfMonth: Moment = momentDate.clone().endOf("month");

  if (absoluteRange) {
    const dateRange: DateRange = {
      from: firstDayOfMonth.toDate(),
      to: lastDayOfMonth.toDate(),
    };

    return dateRange;
  }

  const earliestAvailableDateForMonth: Date = firstDayOfMonth.isBefore(minDate)
    ? minDate.toDate()
    : firstDayOfMonth.toDate();

  const lastAvailableDateForMonth: Date =
    momentDate.isSame(maxDate, "month") && momentDate.isSame(maxDate, "year")
      ? maxDate.toDate()
      : lastDayOfMonth.toDate();

  const dateRange: DateRange = {
    from: earliestAvailableDateForMonth,
    to: lastAvailableDateForMonth,
  };

  return dateRange;
};

export const validatePasswordStrength = (password: string): string | null => {
  if (password.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(password))
    return "Password must contain an uppercase letter";
  if (!/[a-z]/.test(password))
    return "Password must contain a lowercase letter";
  if (!/\d/.test(password)) return "Password must contain a number";
  return null;
};

export const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const renderListDate = (
  msgDate: string,
  format?: string,
): string | undefined => {
  if (!msgDate) return undefined;
  const parsedDate: Moment = format ? moment(msgDate, format) : moment(msgDate);
  const yesterday: Moment = moment().subtract(1, "day");
  if (parsedDate.isSame(moment(), "day")) return "Today";
  if (parsedDate.isSame(yesterday, "day")) return "Yesterday";
  return parsedDate.format(format ? "ddd, ll" : "hh:mm A");
};

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader: FileReader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (readError) => reject(readError);
  });
};

export const validateUploadedFile = (
  file: File,
  accept: string,
  maxSizeMB: number,
): string | null => {
  const acceptedTypesArray: string[] = accept
    .split(",")
    .map((type: string) => type.trim().toLowerCase());
  const fileType: string = file.type.toLowerCase();
  const fileTypeMatch: boolean = acceptedTypesArray.some((type: string) => {
    if (type.includes("*")) {
      const baseType: string = type.split("/")[0];
      return fileType.startsWith(`${baseType}/`);
    }
    return fileType === type;
  });

  if (!fileTypeMatch) {
    return "Unsupported file type";
  }

  if (file.size > maxSizeMB * 1024 * 1024) {
    return `File size exceeds ${maxSizeMB}MB limit`;
  }

  return null;
};

export const triggerTextFileDownload = (
  content: string,
  fileName: string,
  mimeType: string,
): void => {
  const blobFile: Blob = new Blob([content], { type: mimeType });
  const href: string = URL.createObjectURL(blobFile);
  const anchorElement: HTMLAnchorElement = document.createElement("a");
  anchorElement.href = href;
  anchorElement.download = fileName;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  anchorElement.remove();
  URL.revokeObjectURL(href);
};

export const ACCEPTED_DROPZONE_FILE_TYPES: Record<string, string[]> = {
  "text/csv": [".csv"],
  "application/vnd.ms-excel": [".csv"],
};

const ACCEPTED_CSV_MIME_TYPES: ReadonlySet<string> = new Set([
  "text/csv",
  "application/vnd.ms-excel",
]);

export const isCsvMimeType = (file: File): boolean => {
  if (!file.type) {
    const lowercaseFileName: string = file.name.toLowerCase();
    return lowercaseFileName.endsWith(".csv");
  }
  return ACCEPTED_CSV_MIME_TYPES.has(file.type);
};

export const instructionLinesForCSVUpload = (
  sampleFileDownloadUrl: string,
): InstructionLine[] => {
  return [
    { variant: "text", content: Strings.csvUploadInstructionFileType },
    { variant: "text", content: Strings.csvUploadInstructionLimit },
    {
      variant: "download",
      download: {
        prefix: Strings.csvUploadInstructionDownloadLinkPrefix,
        linkText: Strings.csvUploadInstructionDownloadLink,
        suffix: Strings.csvUploadInstructionDownloadSuffix,
        href: sampleFileDownloadUrl,
      },
    },
    { variant: "text", content: Strings.csvUploadInstructionSequence },
  ];
};

export const getContentTypeHeaderValue = (
  headers: AxiosResponse["headers"],
): string => {
  if (!headers) {
    return "";
  }
  return headers["content-type"] ?? headers["Content-Type"] ?? "";
};

export const normalizedPrimaryMimeType = (headerValue: string): string =>
  headerValue.toLowerCase().split(";")[0]?.trim() ?? "";

export const isCsvLikeContentTypeHeader = (headerValue: string): boolean => {
  const primary: string = normalizedPrimaryMimeType(headerValue);
  return primary === "text/csv" || primary === "application/csv";
};

export const isCsvLikeOrPlainTextContentTypeHeader = (
  headerValue: string,
): boolean => {
  if (isCsvLikeContentTypeHeader(headerValue)) {
    return true;
  }
  const primary: string = normalizedPrimaryMimeType(headerValue);
  return primary === "text/plain";
};

export const isPdfLikeContentTypeHeader = (headerValue: string): boolean => {
  const primary: string = normalizedPrimaryMimeType(headerValue);
  return primary === "application/pdf";
};

export const isUtf8ArrayBufferLikelyJson = (buffer: ArrayBuffer): boolean => {
  const maxPeek: number = Math.min(buffer.byteLength, 4096);
  if (maxPeek === 0) {
    return false;
  }
  const text: string = new TextDecoder("utf-8", { fatal: false })
    .decode(buffer.slice(0, maxPeek))
    .trimStart();
  if (text.length === 0) {
    return false;
  }
  const first: string = text[0];
  return first === "{" || first === "[";
};

export const arrayBufferStartsWithPdfMagic = (buffer: ArrayBuffer): boolean => {
  if (buffer.byteLength < 4) {
    return false;
  }
  const view: Uint8Array = new Uint8Array(buffer, 0, 4);
  return (
    view[0] === 0x25 && view[1] === 0x50 && view[2] === 0x44 && view[3] === 0x46
  );
};

export const isStructuredJsonLikeBody = (data: unknown): boolean => {
  if (data === null || typeof data !== "object") {
    return false;
  }
  if (data instanceof Blob) {
    return false;
  }
  if (data instanceof ArrayBuffer) {
    return false;
  }
  if (ArrayBuffer.isView(data)) {
    return false;
  }
  return true;
};

export const downloadArrayBufferAsFile = (
  data: ArrayBuffer,
  fileName: string,
  mimeType: string,
): void => {
  const blobFile: Blob = new Blob([data], { type: mimeType });
  const href: string = URL.createObjectURL(blobFile);
  const anchorElement: HTMLAnchorElement = document.createElement("a");
  anchorElement.download = fileName;
  anchorElement.href = href;
  anchorElement.target = "_blank";
  document.body.appendChild(anchorElement);
  anchorElement.click();
  anchorElement.remove();
  URL.revokeObjectURL(href);
};
